import React, { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import LoadingScreen from './components/LoadingScreen';

const LandingPage        = lazy(() => import('./pages/LandingPage'));
const DynamicCalculator  = lazy(() => import('./pages/DynamicCalculator'));
const ElectionArchive    = lazy(() => import('./pages/ElectionArchive'));
const LearnPage          = lazy(() => import('./pages/LearnPage'));
const AboutPage          = lazy(() => import('./pages/AboutPage'));
const PrivacyPage        = lazy(() => import('./pages/PrivacyPage'));
const TermsPage          = lazy(() => import('./pages/TermsPage'));
const FeedbackPage       = lazy(() => import('./pages/FeedbackPage'));
const NotFoundPage       = lazy(() => import('./pages/NotFoundPage'));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', this.props.label ?? '', error, info);
  }
  render() {
    if (this.state.hasError) {
      const label = this.props.label ?? 'page';
      return (
        <div className="min-h-screen bg-[#0c0c14] flex flex-col items-center justify-center text-white gap-4 px-6 text-center">
          <h1 className="text-2xl font-bold text-violet-300">Something went wrong</h1>
          <p className="text-sm text-violet-200/80">
            An unexpected error occurred in the {label}. The rest of the site is still available.
          </p>
          <a href="/" className="mt-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 rounded-xl text-sm font-semibold transition-colors">
            Return to Map
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function RouteErrorBoundary({ label, children }) {
  return <ErrorBoundary label={label}>{children}</ErrorBoundary>;
}

export default function App() {
  return (
    <HelmetProvider>
    <ErrorBoundary label="app shell">
      <BrowserRouter>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<RouteErrorBoundary label="landing page"><LandingPage /></RouteErrorBoundary>} />
            <Route path="/calculator/:isoCode" element={<RouteErrorBoundary label="calculator"><DynamicCalculator /></RouteErrorBoundary>} />
            <Route path="/archive" element={<RouteErrorBoundary label="election archive"><ElectionArchive /></RouteErrorBoundary>} />
            <Route path="/learn" element={<RouteErrorBoundary label="learn page"><LearnPage /></RouteErrorBoundary>} />
            <Route path="/about" element={<RouteErrorBoundary label="about page"><AboutPage /></RouteErrorBoundary>} />
            <Route path="/privacy" element={<RouteErrorBoundary label="privacy page"><PrivacyPage /></RouteErrorBoundary>} />
            <Route path="/terms" element={<RouteErrorBoundary label="terms page"><TermsPage /></RouteErrorBoundary>} />
            <Route path="/feedback" element={<RouteErrorBoundary label="feedback page"><FeedbackPage /></RouteErrorBoundary>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
    </HelmetProvider>
  );
}
